import { detectOS } from 'detect-browser';

/**
 * Determines if the video is supported by the browser.
 * It first tries to use browser api to determine if the video type is supported -
 * https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/canPlayType.
 * This can return `'probably'`, `'maybe'`, or `''`. Then, based on my experiments if
 * the result is `'probably'` we can just assume it can, if it is `''` we assume it can not,
 * and immediately return the boolean result.
 * If it is `'maybe'` the only way to know is to fetch the video metadata and check, so
 * we return a promise instead.
 */
export const isVideoPlayable =
  (type: string, url: string, timeout = 5000): boolean|Promise<boolean> => {
    // iOS does not seem to support 3gpp but it's impossible to detect as it just fails silently
    if (type === 'video/3gpp' && detectOS(window.navigator.userAgent) === 'iOS') return false;

    const videoElement = document.createElement('video');
    const canPlay = videoElement.canPlayType(type);
    if (canPlay === 'probably') return true;
    if (canPlay === 'maybe') {
      return new Promise((resolve) => {
        const sourceElement = document.createElement('source');
        sourceElement.type = type;
        sourceElement.src = url;
        const deferred = setTimeout(() => resolve(false), timeout);
        videoElement.addEventListener('loadedmetadata', () => {
          clearTimeout(deferred);
          resolve(!!videoElement.videoHeight);
        });
        videoElement.appendChild(sourceElement);
      });
    }

    return false;
  };
